import React, { useEffect, useState, useRef } from 'react';
import { FormField } from './FormField';
import { LockIcon, CreditCardIcon, MailIcon, KeyIcon, FileTextIcon, DollarSignIcon, LinkIcon, SmartphoneIcon, BellIcon, FileIcon, ChevronDownIcon, ChevronRightIcon, CreditCardIcon as SavedCardIcon, GlobeIcon, SearchIcon, XIcon, CheckIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface StandardPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
  onSecurityTypeChange?: (securityType: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => void;
}
export function StandardPayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSecurityTypeChange
}: StandardPaymentProps) {
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('THB');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [storePaymentMethod, setStorePaymentMethod] = useState(false);
  const [selectedPaymentProviders, setSelectedPaymentProviders] = useState<string[]>([]);
  const [securityOption, setSecurityOption] = useState<'public' | 'password' | 'email' | 'sms' | 'document' | 'geo'>('public');
  const [securityEmail, setSecurityEmail] = useState('');
  const [securityPhone, setSecurityPhone] = useState('');
  const [securityPassword, setSecurityPassword] = useState('');
  const [documentType, setDocumentType] = useState('government-id');
  const [customDocumentName, setCustomDocumentName] = useState('');
  const [customDocumentDescription, setCustomDocumentDescription] = useState('');
  const [securitySectionExpanded, setSecuritySectionExpanded] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const countries = [{
    code: 'US',
    name: 'United States',
    flag: '🇺🇸'
  }, {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦'
  }, {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧'
  }, {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺'
  }, {
    code: 'DE',
    name: 'Germany',
    flag: '🇩🇪'
  }, {
    code: 'FR',
    name: 'France',
    flag: '🇫🇷'
  }, {
    code: 'JP',
    name: 'Japan',
    flag: '🇯🇵'
  }, {
    code: 'CN',
    name: 'China',
    flag: '🇨🇳'
  }, {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳'
  }, {
    code: 'BR',
    name: 'Brazil',
    flag: '🇧🇷'
  }, {
    code: 'MX',
    name: 'Mexico',
    flag: '🇲🇽'
  }, {
    code: 'ES',
    name: 'Spain',
    flag: '🇪🇸'
  }, {
    code: 'IT',
    name: 'Italy',
    flag: '🇮🇹'
  }, {
    code: 'NL',
    name: 'Netherlands',
    flag: '🇳🇱'
  }, {
    code: 'SE',
    name: 'Sweden',
    flag: '🇸🇪'
  }, {
    code: 'NO',
    name: 'Norway',
    flag: '🇳🇴'
  }, {
    code: 'DK',
    name: 'Denmark',
    flag: '🇩🇰'
  }, {
    code: 'FI',
    name: 'Finland',
    flag: '🇫🇮'
  }, {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬'
  }, {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '🇦🇪'
  }, {
    code: 'AR',
    name: 'Argentina',
    flag: '🇦🇷'
  }, {
    code: 'AT',
    name: 'Austria',
    flag: '🇦🇹'
  }, {
    code: 'BE',
    name: 'Belgium',
    flag: '🇧🇪'
  }, {
    code: 'CL',
    name: 'Chile',
    flag: '🇨🇱'
  }, {
    code: 'CO',
    name: 'Colombia',
    flag: '🇨🇴'
  }, {
    code: 'CZ',
    name: 'Czech Republic',
    flag: '🇨🇿'
  }, {
    code: 'EG',
    name: 'Egypt',
    flag: '🇪🇬'
  }, {
    code: 'GR',
    name: 'Greece',
    flag: '🇬🇷'
  }, {
    code: 'HK',
    name: 'Hong Kong',
    flag: '🇭🇰'
  }, {
    code: 'HU',
    name: 'Hungary',
    flag: '🇭🇺'
  }, {
    code: 'ID',
    name: 'Indonesia',
    flag: '🇮🇩'
  }, {
    code: 'IE',
    name: 'Ireland',
    flag: '🇮🇪'
  }, {
    code: 'IL',
    name: 'Israel',
    flag: '🇮🇱'
  }, {
    code: 'KR',
    name: 'South Korea',
    flag: '🇰🇷'
  }, {
    code: 'MY',
    name: 'Malaysia',
    flag: '🇲🇾'
  }, {
    code: 'NG',
    name: 'Nigeria',
    flag: '🇳🇬'
  }, {
    code: 'NZ',
    name: 'New Zealand',
    flag: '🇳🇿'
  }, {
    code: 'PH',
    name: 'Philippines',
    flag: '🇵🇭'
  }, {
    code: 'PL',
    name: 'Poland',
    flag: '🇵🇱'
  }, {
    code: 'PT',
    name: 'Portugal',
    flag: '🇵🇹'
  }, {
    code: 'RO',
    name: 'Romania',
    flag: '🇷🇴'
  }, {
    code: 'RU',
    name: 'Russia',
    flag: '🇷🇺'
  }, {
    code: 'SA',
    name: 'Saudi Arabia',
    flag: '🇸🇦'
  }, {
    code: 'CH',
    name: 'Switzerland',
    flag: '🇨🇭'
  }, {
    code: 'TH',
    name: 'Thailand',
    flag: '🇹🇭'
  }, {
    code: 'TR',
    name: 'Turkey',
    flag: '🇹🇷'
  }, {
    code: 'TW',
    name: 'Taiwan',
    flag: '🇹🇼'
  }, {
    code: 'UA',
    name: 'Ukraine',
    flag: '🇺🇦'
  }, {
    code: 'VN',
    name: 'Vietnam',
    flag: '🇻🇳'
  }, {
    code: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦'
  }];
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const filteredCountries = countrySearchQuery ? countries.filter(c => c.name.toLowerCase().includes(countrySearchQuery.toLowerCase()) || c.code.toLowerCase().includes(countrySearchQuery.toLowerCase())) : countries;
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    onExpiryDateChange?.(date);
  };
  const handleAmountChange = (v: string) => {
    setAmount(v);
    onAmountChange?.(v);
  };
  const handleCurrencyChange = (v: string) => {
    setCurrency(v);
    onCurrencyChange?.(v);
  };
  const handleReferenceChange = (v: string) => {
    setReference(v);
    onReferenceChange?.(v);
  };
  const handleSecurityOptionChange = (v: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => {
    setSecurityOption(v);
    onSecurityTypeChange?.(v);
  };
  const toggleCountrySelection = (code: string) => {
    setSelectedCountries(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };
  const removeSelectedCountry = (code: string) => {
    setSelectedCountries(prev => prev.filter(c => c !== code));
  };
  const getCountryByCode = (code: string) => countries.find(c => c.code === code);
  // Shared input class
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
  // Security option summary when collapsed
  const securitySummary = () => {
    if (securityOption === 'public') return null;
    const labels: Record<string, string> = {
      password: 'Password protected',
      email: 'Email verification',
      sms: 'SMS verification',
      document: 'Document verification',
      geo: `Geo-restricted (${selectedCountries.length} ${selectedCountries.length === 1 ? 'country' : 'countries'})`
    };
    return labels[securityOption] || null;
  };
  return <div className="space-y-5" data-id="element-4450">
      {/* ─── PAYMENT DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-4451">
        <div className="p-6 space-y-5" data-id="element-4452">
          {/* Amount + Currency — side by side */}
          <div data-id="element-4453">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-4454">
              Amount <span className="text-red-400" data-id="element-4455">*</span>
            </label>
            <div className="relative" data-id="element-4456">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4457">
                <span className="text-sm font-medium text-gray-500" data-id="element-4458">THB</span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-4459" />
            </div>
          </div>

          {/* Reference + Description — 2-column grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4460">
            <div data-id="element-4461">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-4462">
                Reference
              </label>
              <input type="text" className={inputClass} placeholder="e.g. INV-1234" value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-4463" />
            </div>
            <div data-id="element-4464">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-4465">
                Description
              </label>
              <input type="text" className={inputClass} placeholder="e.g. Monthly subscription" value={description} onChange={e => setDescription(e.target.value)} data-id="element-4466" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── SECURITY SETTINGS ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-4467">
        <button className="w-full text-left flex items-center justify-between px-6 py-4" onClick={() => setSecuritySectionExpanded(!securitySectionExpanded)} data-id="element-4468">
          <div className="flex items-center gap-2" data-id="element-4469">
            <LockIcon size={16} className="text-[#F7941D]" data-id="element-4470" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-4471">
              Security settings
            </span>
            {!securitySectionExpanded && securitySummary() && <span className="text-xs text-gray-500 ml-2 font-normal" data-id="element-4472">
                — {securitySummary()}
              </span>}
          </div>
          {securitySectionExpanded ? <ChevronDownIcon size={16} className="text-gray-400" data-id="element-4473" /> : <ChevronRightIcon size={16} className="text-gray-400" data-id="element-4474" />}
        </button>

        {securitySectionExpanded && <div className="px-6 pb-6" data-id="element-4475">
            <p className="text-xs text-gray-500 mb-4" data-id="element-4476">
              Control how customers access this payment link.
            </p>

            {/* 2-column grid for compact security options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-4477">
              {/* Public */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'public' ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4478">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={securityOption === 'public'} onChange={() => handleSecurityOptionChange('public')} data-id="element-4479" />
                <div className="min-w-0" data-id="element-4480">
                  <div className="flex items-center gap-1.5" data-id="element-4481">
                    <LinkIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-4482" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-4483">
                      Public access
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-4484">
                    Anyone with the link can pay
                  </p>
                </div>
              </label>

              {/* Password */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'password' ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4485">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={securityOption === 'password'} onChange={() => handleSecurityOptionChange('password')} data-id="element-4486" />
                <div className="min-w-0" data-id="element-4487">
                  <div className="flex items-center gap-1.5" data-id="element-4488">
                    <KeyIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-4489" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-4490">
                      Password
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-4491">
                    Require a password to access
                  </p>
                </div>
              </label>

              {/* Email */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'email' ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4492">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={securityOption === 'email'} onChange={() => handleSecurityOptionChange('email')} data-id="element-4493" />
                <div className="min-w-0" data-id="element-4494">
                  <div className="flex items-center gap-1.5" data-id="element-4495">
                    <MailIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-4496" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-4497">
                      Email verification
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-4498">
                    Verify via email code
                  </p>
                </div>
              </label>

              {/* SMS */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'sms' ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4499">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={securityOption === 'sms'} onChange={() => handleSecurityOptionChange('sms')} data-id="element-4500" />
                <div className="min-w-0" data-id="element-4501">
                  <div className="flex items-center gap-1.5" data-id="element-4502">
                    <SmartphoneIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-4503" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-4504">
                      SMS verification
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-4505">
                    Verify via phone number
                  </p>
                </div>
              </label>

              {/* Geo */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'geo' ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4506">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={securityOption === 'geo'} onChange={() => handleSecurityOptionChange('geo')} data-id="element-4507" />
                <div className="min-w-0" data-id="element-4508">
                  <div className="flex items-center gap-1.5" data-id="element-4509">
                    <GlobeIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-4510" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-4511">
                      Geographic restriction
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-4512">
                    Restrict by country
                  </p>
                </div>
              </label>

              {/* Document */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'document' ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4513">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={securityOption === 'document'} onChange={() => handleSecurityOptionChange('document')} data-id="element-4514" />
                <div className="min-w-0" data-id="element-4515">
                  <div className="flex items-center gap-1.5" data-id="element-4516">
                    <FileTextIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-4517" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-4518">
                      Document upload
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-4519">
                    Require document before payment
                  </p>
                </div>
              </label>
            </div>

            {/* Expanded detail for selected security option */}
            {securityOption === 'password' && <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-4520">
                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-4521">
                  Password
                </label>
                <input type="password" className={inputClass} placeholder="Enter a secure password" value={securityPassword} onChange={e => setSecurityPassword(e.target.value)} data-id="element-4522" />
              </div>}

            {securityOption === 'email' && <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-4523">
                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-4524">
                  Customer email
                </label>
                <input type="email" className={inputClass} placeholder="customer@example.com" value={securityEmail} onChange={e => setSecurityEmail(e.target.value)} data-id="element-4525" />
              </div>}

            {securityOption === 'sms' && <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-4526">
                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-4527">
                  Customer phone
                </label>
                <input type="tel" className={inputClass} placeholder="+1 (555) 000-0000" value={securityPhone} onChange={e => setSecurityPhone(e.target.value)} data-id="element-4528" />
              </div>}

            {securityOption === 'geo' && <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-4529">
                <label className="block text-xs font-medium text-gray-600 mb-2" data-id="element-4530">
                  Allowed countries
                </label>

                {selectedCountries.length > 0 && <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-4531">
                    {selectedCountries.map(code => {
              const country = getCountryByCode(code);
              return <span key={code} className="inline-flex items-center gap-1 bg-orange-50 text-orange-800 rounded-full px-2.5 py-1 text-xs" data-id="element-4532">
                          {country?.flag} {country?.name}
                          <button onClick={e => {
                  e.preventDefault();
                  removeSelectedCountry(code);
                }} className="text-orange-500 hover:text-orange-700" data-id="element-4533">
                            <XIcon size={12} data-id="element-4534" />
                          </button>
                        </span>;
            })}
                  </div>}

                <div className="relative" ref={countryDropdownRef} data-id="element-4535">
                  <div className="relative" data-id="element-4536">
                    <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-4537" />
                    <input type="text" className={`${inputClass} pl-9`} placeholder="Search countries..." value={countrySearchQuery} onChange={e => setCountrySearchQuery(e.target.value)} onFocus={() => setShowCountryDropdown(true)} data-id="element-4538" />
                  </div>
                  {showCountryDropdown && <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto" data-id="element-4539">
                      {filteredCountries.length > 0 ? filteredCountries.map(country => <div key={country.code} className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center text-sm ${selectedCountries.includes(country.code) ? 'bg-orange-50' : ''}`} onClick={() => toggleCountrySelection(country.code)} data-id="element-4540">
                            <span data-id="element-4541">
                              {country.flag} {country.name}
                            </span>
                            {selectedCountries.includes(country.code) && <CheckIcon size={14} className="text-[#F7941D]" data-id="element-4542" />}
                          </div>) : <div className="px-3 py-2 text-gray-500 text-sm" data-id="element-4543">
                          No countries found
                        </div>}
                    </div>}
                </div>

                {selectedCountries.length > 0 && <div className="flex justify-end mt-2" data-id="element-4544">
                    <button type="button" className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setSelectedCountries([])} data-id="element-4545">
                      Clear all
                    </button>
                  </div>}
              </div>}

            {securityOption === 'document' && <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100 space-y-3" data-id="element-4546">
                <div className="flex items-start p-3 bg-amber-50 border border-amber-100 rounded-lg" data-id="element-4547">
                  <BellIcon size={13} className="text-amber-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-4548" />
                  <p className="text-xs text-amber-700" data-id="element-4549">
                    You'll be notified when a document is uploaded. Review and
                    approve before the customer can pay.
                  </p>
                </div>

                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-4550">
                  Required document type
                </label>
                <div className="space-y-2" data-id="element-4551">
                  {[{
              id: 'government-id',
              label: "Government-issued ID (Passport, Driver's License)"
            }, {
              id: 'proof-address',
              label: 'Proof of address (Utility bill, Bank statement)'
            }, {
              id: 'business-verification',
              label: 'Business verification (License, Tax ID)'
            }, {
              id: 'custom',
              label: 'Custom document'
            }].map(doc => <label key={doc.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-id="element-4552">
                      <input type="radio" name="documentType" className="h-3.5 w-3.5 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={documentType === doc.id} onChange={() => setDocumentType(doc.id)} data-id="element-4553" />
                      <span className="text-xs" data-id="element-4554">{doc.label}</span>
                    </label>)}
                </div>

                {documentType === 'custom' && <div className="mt-3 pl-5 space-y-3 border-l-2 border-gray-200" data-id="element-4555">
                    <div data-id="element-4556">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-4557">
                        Document name
                      </label>
                      <input type="text" className={inputClass} placeholder="e.g. Proof of insurance" value={customDocumentName} onChange={e => setCustomDocumentName(e.target.value)} data-id="element-4558" />
                    </div>
                    <div data-id="element-4559">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-4560">
                        Instructions for customer
                      </label>
                      <textarea className={`${inputClass} resize-none`} placeholder="Explain what document you need..." rows={2} value={customDocumentDescription} onChange={e => setCustomDocumentDescription(e.target.value)} data-id="element-4561" />
                    </div>
                    <p className="text-[11px] text-gray-400 flex items-center" data-id="element-4562">
                      <FileIcon size={11} className="mr-1" data-id="element-4563" />
                      Shown to the customer on the payment page
                    </p>
                  </div>}
              </div>}
          </div>}
      </div>

      {/* ─── ADDITIONAL OPTIONS ─── */}
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} selectedPaymentProviders={selectedPaymentProviders} onSelectedPaymentProvidersChange={setSelectedPaymentProviders} storePaymentMethod={storePaymentMethod} onStorePaymentMethodChange={setStorePaymentMethod} data-id="element-4564" />
    </div>;
}